import { useDeletePresetMutationApi } from '@aibees/shared/api';
import { useCreateOrUpdatePresetMutation } from '@aibees/shared/api/preset/use-create-or-update-preset-mutation';
import { ParsedPreset, PresetVisibility } from '@aibees/shared/definition';
import { useCloneTemplateItem } from '@aibees/shared/features/advance-table/components/table-layout-modifier-dialog/hooks/use-clone-template-item';

import { TemplateVisibilityTabs } from '@aibees/shared/features/advance-table/components/visibility-tabs';
import { useBoolean, useNotify } from '@aibees/shared/hooks';
import { filterTemplatesByTab } from '@aibees/shared/hooks/use-filter-templates-by-tab';
import { classnames, searchArrayOfObjects } from '@aibees/shared/utils';
import {
  Box,
  Button,
  Card,
  FormControlLabel,
  OutlinedInput,
  Radio,
  Stack,
} from '@mui/material';
import { orderBy, random } from 'lodash-es';
import { memo, useCallback, useMemo, useState } from 'react';
import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { Scrollbar } from '../scrollbar';
import { TemplateManagementDeleteDialog } from '../template-management-delete-dialog';
import { TextEllipsis } from '../text-ellipsis';
import * as Styled from './styles';
import * as SX from './sx';
import type { TemplateManagementProps as Props } from './types';

export const TemplateManagement = memo(
  ({
    className,
    presetType,
    templates,
    tabSize,
    LabelVariant,
    placeholder,
    selectedPreset,
    selectedTemplateId,
    deleteDialogTitle,
    deleteNotificationTitle,
    deleteNotificationDescription,
    deleteNotificationIcon,
    labelWidth,
    labelWidthWithVisibilityIcon,
    onCreate,
    onSelect,
    onEdit,
    onDelete,
  }: Props) => {
    const notify = useNotify();
    const [searchValue, setSearchValue] = useState('');
    const [tabValue, setTabValue] = useState<PresetVisibility>(
      PresetVisibility.ALL
    );
    const openDeleteDialog = useBoolean(false);
    const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
    const [selectedActionItem, setSelectedActionItem] =
      useState<ParsedPreset | null>(null);
    const { mutateAsync, isLoading: isDeleting } = useDeletePresetMutationApi(
      selectedActionItem?.id
    );
    const { mutateAsync: mutateAsyncCreateOrUpdate } =
      useCreateOrUpdatePresetMutation<string>(
        'SELECTED_'.concat(presetType),
        false
      );
    const { handleClone } = useCloneTemplateItem();
    const counts = useMemo(() => {
      const allCount = templates.length;
      const mineCount = templates.filter(
        (t) => t.visibility === PresetVisibility.PRIVATE
      ).length;
      const sharedCount = templates.filter(
        (t) => t.visibility === PresetVisibility.INTERNAL
      ).length;
      const publicCount = templates.filter(
        (t) => t.visibility === PresetVisibility.PUBLIC
      ).length;

      return {
        [PresetVisibility.ALL]: allCount,
        [PresetVisibility.PRIVATE]: mineCount,
        [PresetVisibility.INTERNAL]: sharedCount,
        [PresetVisibility.PUBLIC]: publicCount,
      };
    }, [templates]);

    const handleChangeTab = (
      event: React.SyntheticEvent,
      newValue: PresetVisibility
    ) => {
      setTabValue(newValue as PresetVisibility);
    };

    const filteredTemplates = useMemo(() => {
      const filteredByTab = filterTemplatesByTab(templates ?? [], tabValue);

      const sortedTemplates = orderBy(
        filteredByTab,
        (item) => {
          if (item.value?.default === true) return 2;
          if (item.value?.default === false) return 1;
          return 0;
        },
        'desc'
      );

      return searchArrayOfObjects(sortedTemplates, 'name', searchValue);
    }, [searchValue, tabValue, templates]);

    const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value);
    };

    const handleSelect = (value: ParsedPreset) => {
      onSelect(value);
    };

    const handleOpenDeleteDialog = (value: ParsedPreset) => {
      setSelectedActionItem(value);
      openDeleteDialog.onTrue();
      onDelete?.();
    };

    const handleCopy = (value: ParsedPreset) => {
      handleClone(value);
    };

    const handleEdit = (value: ParsedPreset) => {
      onEdit(value);
    };

    const handleMouseEnter = (itemId: string) => {
      setHoveredItemId(itemId);
    };

    const handleMouseLeave = () => {
      setHoveredItemId(null);
    };

    const getLabelWidth = useCallback(
      (visibility: PresetVisibility | null | undefined) => {
        return visibility === PresetVisibility.PRIVATE
          ? labelWidthWithVisibilityIcon
          : labelWidth;
      },
      [labelWidth, labelWidthWithVisibilityIcon]
    );

    const handleCloseDeleteDialog = useCallback(() => {
      openDeleteDialog.onFalse();
    }, [openDeleteDialog]);

    const handleDelete = useCallback(() => {
      const defaultTemplate = templates.find((t) => t.value.default);
      mutateAsync({}).then((data) => {
        notify.complete(
          deleteNotificationTitle,
          deleteNotificationDescription,
          random(1, 1000000),
          {
            icon: deleteNotificationIcon,
          }
        );

        handleCloseDeleteDialog();
        if (
          selectedActionItem?.id === selectedPreset?.value ||
          templates.length === 2
        ) {
          mutateAsyncCreateOrUpdate(
            defaultTemplate?.id ?? '',
            'SELECTED_'.concat(presetType),
            defaultTemplate?.name ?? ''
          ).then(() => {
            setSelectedActionItem(null);
            onSelect(defaultTemplate!);
          });
        }
      });
    }, [
      templates,
      mutateAsync,
      selectedActionItem?.id,
      selectedPreset?.value,
      notify,
      deleteNotificationTitle,
      deleteNotificationDescription,
      deleteNotificationIcon,
      handleCloseDeleteDialog,
      mutateAsyncCreateOrUpdate,
      presetType,
      onSelect,
    ]);

    return (
      <Styled.Wrapper
        className={classnames('TemplateManagement', className)}
        sx={SX.root}
      >
        <Stack direction="row" gap={0.5} alignItems="center" width="100%">
          <OutlinedInput
            name="search"
            size="small"
            fullWidth
            placeholder={placeholder}
            value={searchValue}
            onChange={handleChangeSearch}
          />

          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<Icon name="plus" />}
            onClick={onCreate}
          >
            Create
          </Button>
        </Stack>
        <Card sx={SX.card}>
          <TemplateVisibilityTabs
            size={tabSize}
            counts={counts}
            tabValue={tabValue}
            LabelVariant={LabelVariant ?? 'soft'}
            onChange={handleChangeTab}
          />
          <Scrollbar sx={SX.scrollbar}>
            <Stack direction="column" gap={0.5} px={0.5}>
              {filteredTemplates.map((item) => (
                <Stack
                  key={item.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  padding={0.5}
                  onMouseEnter={() => handleMouseEnter(item.id)}
                  onMouseLeave={handleMouseLeave}
                  sx={SX.item(hoveredItemId === item.id)}
                >
                  <FormControlLabel
                    value={selectedTemplateId}
                    sx={SX.label}
                    control={
                      <Radio
                        checked={selectedTemplateId === item.id}
                        value={item.id}
                        size="small"
                        disableRipple
                      />
                    }
                    label={
                      <Box
                        overflow="hidden"
                        width={getLabelWidth(item.visibility)}
                      >
                        <TextEllipsis
                          variant="body4"
                          fontWeight={500}
                          color="grey.600"
                        >
                          {item.name}
                        </TextEllipsis>
                      </Box>
                    }
                    onChange={() => handleSelect(item)}
                  />

                  <Stack direction="row" alignItems="center" gap={1}>
                    {item.visibility === PresetVisibility.PRIVATE && (
                      <Icon
                        name="eye-lock-outline"
                        size={16}
                        color="grey.700"
                      />
                    )}

                    <Stack direction="row" alignItems="center">
                      <IconButton
                        icon="copy"
                        iconSize={16}
                        size="xsmall"
                        iconColor="grey.700"
                        onClick={() => handleCopy(item)}
                      />
                      <IconButton
                        icon="edit-outline"
                        iconSize={16}
                        iconColor="grey.700"
                        size="xsmall"
                        disabled={item.value.default}
                        onClick={() => handleEdit(item)}
                      />
                      <IconButton
                        icon="delete"
                        iconSize={16}
                        iconColor="error.500"
                        size="xsmall"
                        disabled={item.value.default}
                        onClick={() => handleOpenDeleteDialog(item)}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Scrollbar>
        </Card>

        <TemplateManagementDeleteDialog
          title={deleteDialogTitle}
          state={openDeleteDialog}
          disabled={isDeleting}
          selectedPreset={selectedPreset}
          selectedTemplateId={selectedActionItem?.id}
          onClose={handleCloseDeleteDialog}
          onDelete={handleDelete}
        />
      </Styled.Wrapper>
    );
  }
);

TemplateManagement.displayName = 'TemplateManagement';
